import { FC, useEffect, useMemo } from 'react';
import { Divider, Stack } from '@mui/material';
import { ROUTES } from 'config';
import {
  AutocompleteInput,
  Button,
  DataLossPrompt,
  DateTimeInput,
  FileInput,
  Footer,
  ImageList,
  Loader,
  MultiLanguageInputContainer,
  NothingFoundAlert,
  NumberInput,
  PageContentLayout,
  PageHeaderLayout,
  Panel,
  SelectInput,
  SideMenu,
  SwitchInput,
  TextField,
} from 'melp-design/components';
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form-latest';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ContentLanguage, FrequencyUnit, frequencyUnits } from 'types/general';
import { Translations } from 'types/Common';
import { usePredefinedToasts } from 'utils/Toast';
import { useRouteParams } from 'utils/useIdParam';
import { Delete } from '@mui/icons-material';
import { formatLanguage } from 'utils/format';
import { useUploadFile } from 'store/files';
import { useCompanyLanguages, useMe } from 'state/Administrators';
import { useEmployeeGroups } from 'store/employee-groups';
import {
  useCreateRecognitionAchievement,
  useRecognitionAchievement,
  useUpdateRecognitionAchievement,
} from 'store/recognition-achievements';
import { usePointsLabel } from 'store/recognitions';

interface Values {
  languages: Array<Uppercase<ContentLanguage>>;
  name: string;
  points: number;
  requiresApproval: boolean;
  limit: number;
  limitPeriod: FrequencyUnit;
  imageId: string;
  translations: Translations<{ title: string; description: string }>;
  employeeGroups: string[];
  isActive: boolean;
  startDate: string | null;
  endDate: string | null;
}

const getInitialValues = (
  defaultLanguage: Uppercase<ContentLanguage>,
): Values => ({
  languages: [defaultLanguage],
  name: '',
  points: NaN,
  requiresApproval: false,
  limit: 0,
  limitPeriod: 'monthly',
  imageId: '',
  translations: [{ language: defaultLanguage, title: '', description: '' }],
  employeeGroups: [],
  isActive: true,
  startDate: null,
  endDate: null,
});

export const AchievementDetails: FC = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const { path } = useRouteMatch();
  const { hasEditPermission } = useMe();
  const canEdit = hasEditPermission(['employeeRecognition']);
  const { id: achievementId } = useRouteParams(
    ROUTES.recognitions.achievements.details,
  );
  const predefinedToasts = usePredefinedToasts();
  const { defaultLanguage, supportedLanguages } = useCompanyLanguages();
  const pointsLabel = usePointsLabel();

  const INITIAL_VALUES = useMemo<Values>(
    () => getInitialValues(defaultLanguage),
    [defaultLanguage],
  );

  const { data: achievement, isLoading } =
    useRecognitionAchievement(achievementId);
  const { data: employeesGroups, isLoading: isEmployeeGroupsLoading } =
    useEmployeeGroups();

  const {
    control,
    handleSubmit,
    getValues,
    watch,
    reset,
    formState: { dirtyFields },
  } = useForm<Values>({
    defaultValues: INITIAL_VALUES,
  });
  const { fields: translationsFields, replace: replaceTranslations } =
    useFieldArray({ name: 'translations', control });

  useEffect(() => {
    if (achievement) {
      reset({
        languages: achievement.translations.map((trans) => trans.language),
        name: achievement.name,
        points: achievement.points,
        requiresApproval: achievement.requiresApproval,
        limit: achievement.limit,
        limitPeriod: achievement.limitPeriod,
        imageId: achievement.image?.id ?? '',
        employeeGroups: achievement.employeeGroups.map((group) => group.id),
        translations: achievement.translations,
        isActive: achievement.isActive,
        startDate: achievement.startDate,
        endDate: achievement.endDate,
      });
    }
  }, [achievement, reset]);

  const { mutate: uploadFile, isLoading: isUploading } = useUploadFile();
  const { mutate: createAchievement, isLoading: isCreating } =
    useCreateRecognitionAchievement();
  const { mutate: updateAchievement, isLoading: isUpdating } =
    useUpdateRecognitionAchievement();

  const onSubmit: SubmitHandler<Values> = (values) => {
    const valuesToData = {
      name: values.name,
      points: values.points,
      requiresApproval: values.requiresApproval,
      limit: values.limit,
      limitPeriod: values.limitPeriod,
      imageId: values.imageId,
      employeeGroups: values.employeeGroups,
      isActive: values.isActive,
      startDate: values.startDate,
      endDate: values.endDate,
      translations: values.translations.map((trans) => ({
        language: trans.language,
        title: trans.title,
        description: trans.description,
      })),
    } as const;

    if (achievement) {
      updateAchievement(
        { achievementId, data: valuesToData },
        {
          onSuccess: () => {
            predefinedToasts.success.updated();
            history.push(ROUTES.recognitions.achievements.list);
          },
        },
      );
    } else {
      createAchievement(valuesToData, {
        onSuccess: () => {
          predefinedToasts.success.added();
          history.push(ROUTES.recognitions.achievements.list);
        },
      });
    }
  };

  const isDirty = !!Object.keys(dirtyFields).length;

  const [imageId] = watch(['imageId']);

  if (achievementId && isLoading) return <Loader />;
  if (achievementId && !achievement) return <NothingFoundAlert />;

  return (
    <Stack gap={2}>
      <PageHeaderLayout
        breadcrumbs={[
          {
            label: t('recognition.recognitionSettings'),
            url: ROUTES.recognitions.list,
          },
          {
            label: t('recognition.achievementsTab'),
            url: ROUTES.recognitions.achievements.list,
          },
          {
            label: !achievementId
              ? t('common.create')
              : achievement
              ? achievement.name
              : '',
          },
        ]}
      />

      <PageContentLayout
        sidebar={
          <SideMenu
            tabs={[
              {
                key: 'settings',
                label: t('common.settings'),
                url: achievementId
                  ? ROUTES.recognitions.achievements.details.replace(
                      ':id',
                      achievementId,
                    )
                  : ROUTES.recognitions.achievements.create,
                isActive:
                  path === ROUTES.recognitions.achievements.details ||
                  path === ROUTES.recognitions.achievements.create,
                disabled: !achievement,
              },
            ]}
          />
        }
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <Panel title={t('benefits.benefitGeneralSettingsSection')}>
              <Stack spacing={2}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <TextField
                      label={t('common.systemName')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      required
                      disabled={!canEdit}
                      ref={ref}
                    />
                  )}
                />

                <Controller
                  name="isActive"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => {
                    return (
                      <SwitchInput
                        label={t(`status.${value ? 'active' : 'inactive'}`)}
                        name={name}
                        value={value}
                        onChange={onChange}
                        disabled={!canEdit}
                        ref={ref}
                      />
                    );
                  }}
                />

                <Stack flexDirection="row" gap={2}>
                  <Controller
                    name="startDate"
                    control={control}
                    render={({ field: { name, value, onChange, ref } }) => (
                      <DateTimeInput
                        label={t('benefits.published_from')}
                        name={name}
                        value={value}
                        onChange={onChange}
                        datePickerProps={{
                          showTimeOffset: true,
                        }}
                        disabled={!canEdit}
                        ref={ref}
                      />
                    )}
                  />

                  <Divider orientation="vertical" variant="middle" flexItem />

                  <Controller
                    name="endDate"
                    control={control}
                    render={({ field: { name, value, onChange, ref } }) => (
                      <DateTimeInput
                        label={t('benefits.inactive_from')}
                        name={name}
                        value={value}
                        onChange={onChange}
                        datePickerProps={{
                          showTimeOffset: true,
                        }}
                        disabled={!canEdit}
                        ref={ref}
                      />
                    )}
                  />
                </Stack>

                <Controller
                  name="points"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <NumberInput
                      label={t('recognition.achievementPoints', {
                        recognitionCurrency: pointsLabel,
                      })}
                      name={name}
                      value={value}
                      onChange={onChange}
                      key={value}
                      required
                      disabled={!canEdit}
                      ref={ref}
                    />
                  )}
                />

                <Stack gap={2} flexDirection="row">
                  <Controller
                    name="limit"
                    control={control}
                    render={({ field: { name, value, onChange, ref } }) => {
                      return (
                        <NumberInput
                          label={t('recognition.achievementAssignmentLimit')}
                          name={name}
                          value={value}
                          onChange={onChange}
                          type="integer"
                          key={value}
                          sx={{ width: '100%' }}
                          infoMessage={t(
                            'recognition.achievementAssignmentLimitHint',
                          )}
                          disabled={!canEdit}
                          ref={ref}
                        />
                      );
                    }}
                  />

                  <Controller
                    name="limitPeriod"
                    control={control}
                    render={({ field: { name, value, onChange, ref } }) => {
                      return (
                        <SelectInput
                          name={name}
                          value={value}
                          onChange={onChange}
                          label={t('recognition.allowanceRulesListFrequency')}
                          options={frequencyUnits
                            .filter((period) =>
                              ['weekly', 'monthly', 'yearly'].includes(period),
                            )
                            .map((period) => ({
                              label: t(
                                `recognition.allowanceRuleFrequency-${period}`,
                              ),
                              value: period,
                            }))}
                          disabled={!canEdit}
                          ref={ref}
                        />
                      );
                    }}
                  />
                </Stack>

                <Controller
                  name="requiresApproval"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <SwitchInput
                      label={t('recognition.achievementRequiresApproval')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      disabled={!canEdit}
                      ref={ref}
                    />
                  )}
                />

                <Controller
                  name="employeeGroups"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => {
                    return (
                      <AutocompleteInput
                        label={t('recognition.achievementAccessibleByGroups')}
                        name={name}
                        value={value}
                        onChange={onChange}
                        options={
                          employeesGroups?.items.map((group) => ({
                            label: group.name,
                            value: group.id,
                          })) ?? []
                        }
                        disabled={isEmployeeGroupsLoading || !canEdit}
                        multiple
                        required
                        ref={ref}
                      />
                    );
                  }}
                />

                <Controller
                  name="imageId"
                  control={control}
                  render={({ field: { name, onChange, ref } }) => {
                    return imageId ? (
                      <ImageList
                        items={[
                          {
                            imageId,
                            actions: [
                              {
                                icon: Delete,
                                variant: 'neutral-outline',
                                disabled: !canEdit,
                                onClick: () => onChange(''),
                              },
                            ],
                          },
                        ]}
                      />
                    ) : (
                      <FileInput
                        label={t('recognition.achievementImage')}
                        name={name}
                        onChange={([file]) => {
                          if (file) {
                            uploadFile(
                              { file },
                              {
                                onSuccess: ({ data }) => {
                                  onChange(data.id);
                                },
                              },
                            );
                          } else {
                            onChange('');
                          }
                        }}
                        disabled={isUploading || !canEdit}
                        maxFiles={1}
                        previewSelectedFiles={false}
                        accept={['jpg', 'jpeg', 'png', 'gif', 'webp']}
                        ref={ref}
                      />
                    );
                  }}
                />
              </Stack>
            </Panel>

            <Panel title={t('benefitTemplates.templateContent')}>
              <Stack gap={2}>
                <Controller
                  name="languages"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => {
                    return (
                      <SelectInput
                        label={t('companies.supported_languages')}
                        name={name}
                        value={value}
                        onChange={(value) => {
                          const selectedLanguages = value as Array<
                            Uppercase<ContentLanguage>
                          >;
                          replaceTranslations(
                            selectedLanguages.map(
                              (language) =>
                                getValues('translations').find(
                                  (t) => t.language === language,
                                ) ?? {
                                  language,
                                  title: '',
                                  description: '',
                                },
                            ),
                          );
                          onChange(selectedLanguages);
                        }}
                        options={supportedLanguages.map((language) => ({
                          value: language,
                          label: formatLanguage(i18n.language, language),
                          disabled: language === defaultLanguage,
                        }))}
                        multiple
                        required
                        disabled={!canEdit}
                        ref={ref}
                      />
                    );
                  }}
                />

                <MultiLanguageInputContainer
                  label={t('recognition.achievementTitle')}
                  info={t('recognition.achievementTitleHint')}
                  required
                  fields={translationsFields.map((field, i) => ({
                    id: field.id,
                    language: field.language,
                    component: (
                      <Controller
                        name={`translations.${i}.title`}
                        control={control}
                        render={({ field: { name, value, onChange, ref } }) => {
                          return (
                            <TextField
                              name={name}
                              value={value}
                              onChange={onChange}
                              required
                              disabled={!canEdit}
                              ref={ref}
                            />
                          );
                        }}
                      />
                    ),
                  }))}
                />

                <MultiLanguageInputContainer
                  label={t('recognition.achievementDescription')}
                  info={t('recognition.achievementDescriptionHint')}
                  fields={translationsFields.map((field, i) => ({
                    id: field.id,
                    language: field.language,
                    component: (
                      <Controller
                        name={`translations.${i}.description`}
                        control={control}
                        render={({ field: { name, value, onChange, ref } }) => {
                          return (
                            <TextField
                              name={name}
                              value={value}
                              onChange={onChange}
                              rows={3}
                              disabled={!canEdit}
                              ref={ref}
                            />
                          );
                        }}
                      />
                    ),
                  }))}
                />
              </Stack>
            </Panel>
          </Stack>

          <Footer visible={isDirty}>
            <Stack direction="row" justifyContent="end" gap="10px">
              <Button
                label={t('common.cancel')}
                variant="neutral-outline"
                onClick={() => {
                  reset(undefined, { keepDirty: false });
                }}
              />
              <Button
                label={t('common.save')}
                type="submit"
                disabled={
                  isCreating ||
                  isUpdating ||
                  !hasEditPermission(['employeeRecognition'])
                }
              />
            </Stack>
          </Footer>
        </form>

        <DataLossPrompt
          when={
            !hasEditPermission(['employeeRecognition'])
              ? false
              : isDirty && !isUpdating && !isCreating
          }
        />
      </PageContentLayout>
    </Stack>
  );
};
